import {
  emptyPaidBillingVariants,
  parseBrlMoneyStringToCents,
  planPriceCentsToFormString,
  PLAN_BILLING_PERIOD_LABELS,
  PLAN_BILLING_PERIODS,
  STRIPE_PRICE_ID_PATTERN,
  type Plan,
  type PlanBillingPeriod,
  type PlanBillingVariant,
  type PlanKind,
  type PlanStripe,
} from '@/core/domain';
import {
  PLAN_ADMIN_VALIDATION_FREE_MAX_SERVICES,
  PLAN_ADMIN_VALIDATION_SORT,
  PLAN_ADMIN_VALIDATION_STRIPE_ID,
  PLAN_ADMIN_VALIDATION_STRIPE_TEST_ID,
  PLAN_ADMIN_VALIDATION_TITLE,
  PLAN_ADMIN_VALIDATION_VARIANT_PRICE,
} from '@/features/plans/presentation/composables/constants/plan-admin.constants';

export type PlanVariantFormSlice = {
  priceText: string;
  stripePriceId: string;
  stripePriceIdTest: string;
  stripeLookupKey: string;
  stripeLookupKeyTest: string;
};

export type PlanFormState = {
  kind: PlanKind;
  title: string;
  sort_order: number;
  is_active: boolean;
  max_services: number;
  variants: Record<PlanBillingPeriod, PlanVariantFormSlice>;
};

function emptyVariantSlice(cents = 0): PlanVariantFormSlice {
  return {
    priceText: planPriceCentsToFormString(cents),
    stripePriceId: '',
    stripePriceIdTest: '',
    stripeLookupKey: '',
    stripeLookupKeyTest: '',
  };
}

export function emptyPlanForm(kind: PlanKind = 'paid'): PlanFormState {
  const variants = {} as Record<PlanBillingPeriod, PlanVariantFormSlice>;
  for (const period of PLAN_BILLING_PERIODS) {
    variants[period] = emptyVariantSlice();
  }
  return {
    kind,
    title: '',
    sort_order: kind === 'free' ? 0 : 1,
    is_active: true,
    max_services: kind === 'free' ? 10 : 0,
    variants,
  };
}

function variantToFormSlice(variant: PlanBillingVariant): PlanVariantFormSlice {
  const { stripe } = variant;
  return {
    priceText: planPriceCentsToFormString(variant.price_brl_cents),
    stripePriceId: stripe.stripe_price_id ?? '',
    stripePriceIdTest: stripe.stripe_price_id_test ?? '',
    stripeLookupKey: stripe.stripe_price_lookup_key ?? '',
    stripeLookupKeyTest: stripe.stripe_price_lookup_key_test ?? '',
  };
}

export function planToFormState(plan: Plan): PlanFormState {
  const base = emptyPlanForm(plan.kind);
  base.title = plan.title;
  base.sort_order = plan.sort_order;
  base.is_active = plan.is_active;
  base.max_services = plan.free_limits?.max_services ?? (plan.kind === 'free' ? 10 : 0);

  if (plan.kind === 'paid' && plan.billing_variants) {
    for (const period of PLAN_BILLING_PERIODS) {
      const v = plan.billing_variants[period];
      if (v) base.variants[period] = variantToFormSlice(v);
    }
  }

  return base;
}

function sliceToStripe(slice: PlanVariantFormSlice): PlanStripe {
  const stripePriceIdTrim = slice.stripePriceId.trim();
  const stripePriceIdTestTrim = slice.stripePriceIdTest.trim();
  const stripeLookupTrim = slice.stripeLookupKey.trim();
  const stripeLookupTestTrim = slice.stripeLookupKeyTest.trim();

  return {
    stripe_price_id: stripePriceIdTrim.length > 0 ? stripePriceIdTrim : null,
    stripe_price_id_test: stripePriceIdTestTrim.length > 0 ? stripePriceIdTestTrim : null,
    stripe_price_lookup_key: stripeLookupTrim.length > 0 ? stripeLookupTrim : null,
    stripe_price_lookup_key_test: stripeLookupTestTrim.length > 0 ? stripeLookupTestTrim : null,
  };
}

export type PlanFormValidationResult =
  | {
      ok: true;
      billing_variants?: Record<PlanBillingPeriod, PlanBillingVariant>;
      free_limits?: { max_services: number };
    }
  | { ok: false; message: string };

export function validatePlanForm(form: PlanFormState): PlanFormValidationResult {
  const title = form.title.trim();
  if (!title) {
    return { ok: false, message: PLAN_ADMIN_VALIDATION_TITLE };
  }

  const sort = Number(form.sort_order);
  if (!Number.isInteger(sort) || sort < 0) {
    return { ok: false, message: PLAN_ADMIN_VALIDATION_SORT };
  }

  if (form.kind === 'free') {
    const maxServices = Number(form.max_services);
    if (!Number.isInteger(maxServices) || maxServices < 0) {
      return { ok: false, message: PLAN_ADMIN_VALIDATION_FREE_MAX_SERVICES };
    }
    return { ok: true, free_limits: { max_services: maxServices } };
  }

  const billing_variants: Record<PlanBillingPeriod, PlanBillingVariant> = emptyPaidBillingVariants();

  for (const period of PLAN_BILLING_PERIODS) {
    const label = PLAN_BILLING_PERIOD_LABELS[period];
    const slice = form.variants[period];
    const priceCents = parseBrlMoneyStringToCents(slice.priceText);
    if (priceCents === null) {
      return { ok: false, message: PLAN_ADMIN_VALIDATION_VARIANT_PRICE(label) };
    }

    const stripePriceIdTrim = slice.stripePriceId.trim();
    const stripePriceIdTestTrim = slice.stripePriceIdTest.trim();
    if (stripePriceIdTrim.length > 0 && !STRIPE_PRICE_ID_PATTERN.test(stripePriceIdTrim)) {
      return { ok: false, message: PLAN_ADMIN_VALIDATION_STRIPE_ID(label) };
    }
    if (stripePriceIdTestTrim.length > 0 && !STRIPE_PRICE_ID_PATTERN.test(stripePriceIdTestTrim)) {
      return { ok: false, message: PLAN_ADMIN_VALIDATION_STRIPE_TEST_ID(label) };
    }

    billing_variants[period] = {
      price_brl_cents: priceCents,
      stripe: sliceToStripe(slice),
    };
  }

  return { ok: true, billing_variants };
}

export function buildPlanFromForm(
  form: PlanFormState,
  existing: Plan | null
): { plan: Plan | null; error: string | null } {
  const validation = validatePlanForm(form);
  if (!validation.ok) {
    return { plan: null, error: validation.message };
  }

  const now = new Date().toISOString();
  const id = existing?.id ?? crypto.randomUUID();

  const plan: Plan = {
    id,
    kind: form.kind,
    title: form.title.trim(),
    sort_order: Number(form.sort_order),
    is_active: form.is_active,
    created_at: existing?.created_at ?? now,
    updated_at: now,
    ...(form.kind === 'paid' && validation.billing_variants ?
      { billing_variants: validation.billing_variants }
    : {}),
    ...(form.kind === 'free' && validation.free_limits ? { free_limits: validation.free_limits } : {}),
  };

  return { plan, error: null };
}
